import React, { createContext, useContext, useState, useMemo, useCallback } from "react";
import { DEFAULT_INPUTS, PRESET_SCENARIOS, ENERGY_SCENARIOS } from "@/lib/defaults";
import { calcSummary, calc5YearProjection, calcPaybackDynamic, calcIRR, runOptimizer, auditModel, rankNewLotsExpansion } from "@/lib/calc";

const SimulatorContext = createContext(null);
const clone = (obj) => JSON.parse(JSON.stringify(obj));

export const SimulatorProvider = ({ children }) => {
  const [inputs, setInputs] = useState(clone(DEFAULT_INPUTS));
  const [activeScenarioName, setActiveScenarioName] = useState("Scenario Base (300 m² · 36 m³)");

  const summary = useMemo(() => calcSummary(inputs), [inputs]);
  const projection = useMemo(() => calc5YearProjection(inputs), [inputs]);
  const payback = useMemo(() => calcPaybackDynamic(inputs), [inputs]);
  const irr = useMemo(() => calcIRR(inputs), [inputs]);
  const optimizer = useMemo(() => runOptimizer(inputs), [inputs]);
  const audit = useMemo(() => auditModel(inputs, calcSummary(inputs)), [inputs]);
  const expansionRanking = useMemo(() => rankNewLotsExpansion(summary.newLots || { items: [] }), [summary]);

  const updateNewLot = useCallback((key, patch) => {
    setInputs((prev) => ({
      ...prev,
      new_lots: { ...prev.new_lots, [key]: { ...prev.new_lots[key], ...patch } },
    }));
  }, []);

  const updateNewLotSpecies = useCallback((key, speciesKey, patch) => {
    setInputs((prev) => {
      const lot = prev.new_lots[key];
      return {
        ...prev,
        new_lots: {
          ...prev.new_lots,
          [key]: {
            ...lot,
            species: lot.species.map((s) => (s.key === speciesKey ? { ...s, ...patch } : s)),
          },
        },
      };
    });
  }, []);

  const updateNewLotPath = useCallback((key, pathKey, patch) => {
    setInputs((prev) => {
      const lot = prev.new_lots[key];
      return {
        ...prev,
        new_lots: {
          ...prev.new_lots,
          [key]: {
            ...lot,
            commercialization_path: lot.commercialization_path.map((p) => (p.key === pathKey ? { ...p, ...patch } : p)),
          },
        },
      };
    });
  }, []);

  const updateSaffron = useCallback((patch) => {
    setInputs((prev) => ({ ...prev, saffron: { ...prev.saffron, ...patch } }));
  }, []);

  const updateAlgaeSpecies = useCallback((speciesKey, patch) => {
    setInputs((prev) => ({
      ...prev,
      algae: { ...prev.algae, [speciesKey]: { ...prev.algae[speciesKey], ...patch } },
    }));
  }, []);

  const updateAlgaeConfig = useCallback((patch) => {
    setInputs((prev) => ({ ...prev, algae: { ...prev.algae, ...patch } }));
  }, []);

  const updateOpex = useCallback((patch) => {
    setInputs((prev) => ({ ...prev, opex: { ...prev.opex, ...patch } }));
  }, []);

  const updateCapex = useCallback((patch) => {
    setInputs((prev) => ({ ...prev, capex: { ...prev.capex, ...patch } }));
  }, []);

  const updateEnergy = useCallback((patch) => {
    setInputs((prev) => ({ ...prev, energy: { ...prev.energy, ...patch } }));
  }, []);

  const updateSubsystem = useCallback((key, patch) => {
    setInputs((prev) => ({
      ...prev,
      energy: {
        ...prev.energy,
        subsystems: {
          ...prev.energy.subsystems,
          [key]: { ...prev.energy.subsystems[key], ...patch },
        },
      },
    }));
  }, []);

  const updateBattery = useCallback((patch) => {
    setInputs((prev) => ({
      ...prev,
      energy: { ...prev.energy, battery: { ...prev.energy.battery, ...patch } },
    }));
  }, []);

  const updateElectrolyzer = useCallback((patch) => {
    setInputs((prev) => ({
      ...prev,
      energy: { ...prev.energy, electrolyzer: { ...prev.energy.electrolyzer, ...patch } },
    }));
  }, []);

  const updateFuelCell = useCallback((patch) => {
    setInputs((prev) => ({
      ...prev,
      energy: { ...prev.energy, fuel_cell: { ...prev.energy.fuel_cell, ...patch } },
    }));
  }, []);

  const loadEnergyScenario = useCallback((key) => {
    const sc = ENERGY_SCENARIOS[key];
    if (!sc) return;
    setInputs((prev) => {
      const newSubs = { ...prev.energy.subsystems };
      Object.entries(sc.subsystems).forEach(([sk, patch]) => {
        newSubs[sk] = { ...newSubs[sk], ...patch };
      });
      return {
        ...prev,
        energy: { ...prev.energy, subsystems: newSubs, active_scenario: key },
      };
    });
  }, []);

  // Legacy support: updatePVSurface / addPVSurface / removePVSurface / updateLoadCategory
  const updatePVSurface = useCallback(() => {}, []);
  const addPVSurface = useCallback(() => {}, []);
  const removePVSurface = useCallback(() => {}, []);
  const updateLoadCategory = useCallback((id, patch) => {
    setInputs((prev) => ({
      ...prev,
      energy: {
        ...prev.energy,
        load_categories: (prev.energy.load_categories || []).map((l) =>
          l.id === id ? { ...l, ...patch } : l
        ),
      },
    }));
  }, []);

  const addLoadCategory = useCallback((cat) => {
    setInputs((prev) => ({
      ...prev,
      energy: {
        ...prev.energy,
        load_categories: [
          ...(prev.energy.load_categories || []),
          { id: `load${Date.now()}`, kwh_year: 0, classification: "USER_INPUT", ...cat },
        ],
      },
    }));
  }, []);

  const removeLoadCategory = useCallback((id) => {
    setInputs((prev) => ({
      ...prev,
      energy: {
        ...prev.energy,
        load_categories: (prev.energy.load_categories || []).filter((l) => l.id !== id),
      },
    }));
  }, []);

  const updateProjection = useCallback((patch) => {
    setInputs((prev) => ({ ...prev, projection: { ...prev.projection, ...patch } }));
  }, []);

  const updateFutureLot = useCallback((id, patch) => {
    setInputs((prev) => ({
      ...prev,
      future_lots: (prev.future_lots || []).map((l) => (l.id === id ? { ...l, ...patch } : l)),
    }));
  }, []);

  const addFutureLot = useCallback((lot) => {
    setInputs((prev) => ({
      ...prev,
      future_lots: [...(prev.future_lots || []), { id: `lot${Date.now()}`, enabled: true, ...lot }],
    }));
  }, []);

  const removeFutureLot = useCallback((id) => {
    setInputs((prev) => ({
      ...prev,
      future_lots: (prev.future_lots || []).filter((l) => l.id !== id),
    }));
  }, []);

  const loadPreset = useCallback((presetKey) => {
    const preset = PRESET_SCENARIOS[presetKey];
    if (!preset) return;
    setInputs(clone(preset.inputs));
    setActiveScenarioName(preset.name);
  }, []);

  const loadInputs = useCallback((newInputs, name = "Scenario Caricato") => {
    setInputs(clone(newInputs));
    setActiveScenarioName(name);
  }, []);

  const resetInputs = useCallback(() => {
    setInputs(clone(DEFAULT_INPUTS));
    setActiveScenarioName("Scenario Base (300 m² · 36 m³)");
  }, []);

  const value = {
    inputs, summary, projection, payback, irr, optimizer, audit, expansionRanking,
    activeScenarioName, setActiveScenarioName,
    updateSaffron, updateAlgaeSpecies, updateAlgaeConfig,
    updateOpex, updateCapex, updateProjection,
    updateEnergy, updateSubsystem, updateBattery, updateElectrolyzer, updateFuelCell,
    loadEnergyScenario,
    updatePVSurface, addPVSurface, removePVSurface, updateLoadCategory,
    addLoadCategory, removeLoadCategory,
    updateFutureLot, addFutureLot, removeFutureLot,
    updateNewLot, updateNewLotSpecies, updateNewLotPath,
    loadPreset, loadInputs, resetInputs,
  };

  return <SimulatorContext.Provider value={value}>{children}</SimulatorContext.Provider>;
};

export const useSimulator = () => {
  const ctx = useContext(SimulatorContext);
  if (!ctx) throw new Error("useSimulator must be used within SimulatorProvider");
  return ctx;
};
